// TelcoTrack — barevný kód optických vláken (profil × druh kabelu).
// Fyzické číslo = (buffer, barva, proužek). Buffery po 24 vláknech, proužek na pozicích 13–24.

export const FIBER_COLOR_PROFILES = ['DEFAULT', 'T_MOBILE_CZ'] as const;
export type FiberColorProfileId = (typeof FIBER_COLOR_PROFILES)[number];

export const FIBER_CABLE_KINDS = ['OUTER', 'RISER', 'BUNDLE'] as const;
export type FiberCableKind = (typeof FIBER_CABLE_KINDS)[number];

export const FIBER_COLOR_IDS = [
  'MODRA',
  'ORANZOVA',
  'ZELENA',
  'TMAVE_ZELENA',
  'SVETLE_ZELENA',
  'SVETLE_MODRA',
  'HNEDA',
  'SEDA',
  'BILA',
  'CERVENA',
  'CERNA',
  'ZLUTA',
  'FIALOVA',
  'RUZOVA',
  'TYRKYSOVA',
] as const;
export type FiberColorId = (typeof FIBER_COLOR_IDS)[number];

export const FIBERS_PER_BUFFER = 24;
export const FIBER_STRIPE_FROM_POSITION = 13;
export const FIBER_BUFFER_COUNT = 6;
export const FIBER_NUMBER_MAX = FIBER_BUFFER_COUNT * FIBERS_PER_BUFFER;
export const FIBER_COLORS_PER_GROUP = 12;

/** Společné pořadí bufferů pro 1–144 (modrá … světle modrá … bílá). */
export const FIBER_BUFFER_COLORS = [
  'MODRA',
  'ORANZOVA',
  'ZELENA',
  'HNEDA',
  'SVETLE_MODRA',
  'BILA',
] as const satisfies readonly FiberColorId[];

export type FiberBufferColorId = (typeof FIBER_BUFFER_COLORS)[number];

const OUTER_12 = [
  'MODRA',
  'ORANZOVA',
  'ZELENA',
  'HNEDA',
  'SEDA',
  'BILA',
  'CERVENA',
  'CERNA',
  'ZLUTA',
  'FIALOVA',
  'RUZOVA',
  'TYRKYSOVA',
] as const satisfies readonly FiberColorId[];

const RISER_12 = [
  'MODRA',
  'ORANZOVA',
  'TMAVE_ZELENA',
  'CERVENA',
  'FIALOVA',
  'SEDA',
  'ZLUTA',
  'HNEDA',
  'SVETLE_ZELENA',
  'BILA',
  'RUZOVA',
  'TYRKYSOVA',
] as const satisfies readonly FiberColorId[];

const BUNDLE_12 = [
  'MODRA',
  'ORANZOVA',
  'ZELENA',
  'CERVENA',
  'FIALOVA',
  'SEDA',
  'ZLUTA',
  'HNEDA',
  'CERNA',
  'BILA',
  'RUZOVA',
  'TYRKYSOVA',
] as const satisfies readonly FiberColorId[];

export type FiberColorOrder12 = readonly FiberColorId[] & { readonly length: 12 };

const PROFILE_ORDERS: Record<FiberColorProfileId, Record<FiberCableKind, FiberColorOrder12>> = {
  DEFAULT: {
    OUTER: OUTER_12,
    RISER: RISER_12,
    BUNDLE: BUNDLE_12,
  },
  T_MOBILE_CZ: {
    OUTER: OUTER_12,
    RISER: RISER_12,
    BUNDLE: BUNDLE_12,
  },
};

export const FIBER_COLOR_LABELS_CS: Record<FiberColorId, string> = {
  MODRA: 'modrá',
  ORANZOVA: 'oranžová',
  ZELENA: 'zelená',
  TMAVE_ZELENA: 'tmavě zelená',
  SVETLE_ZELENA: 'světle zelená',
  SVETLE_MODRA: 'světle modrá',
  HNEDA: 'hnědá',
  SEDA: 'šedá',
  BILA: 'bílá',
  CERVENA: 'červená',
  CERNA: 'černá / přírodní',
  ZLUTA: 'žlutá',
  FIALOVA: 'fialová',
  RUZOVA: 'růžová',
  TYRKYSOVA: 'transparentní / tyrkysová',
};

export const FIBER_COLOR_HEX: Record<FiberColorId, string> = {
  MODRA: '#2563EB',
  ORANZOVA: '#EA580C',
  ZELENA: '#16A34A',
  TMAVE_ZELENA: '#166534',
  SVETLE_ZELENA: '#86EFAC',
  SVETLE_MODRA: '#7DD3FC',
  HNEDA: '#92400E',
  SEDA: '#6B7280',
  BILA: '#F8FAFC',
  CERVENA: '#DC2626',
  CERNA: '#111827',
  ZLUTA: '#EAB308',
  FIALOVA: '#7C3AED',
  RUZOVA: '#DB2777',
  TYRKYSOVA: '#0D9488',
};

export const FIBER_COLOR_PROFILE_LABELS_CS: Record<FiberColorProfileId, string> = {
  DEFAULT: 'Výchozí (mimo T-Mobile)',
  T_MOBILE_CZ: 'T-Mobile CZ',
};

export const FIBER_CABLE_KIND_LABELS_CS: Record<FiberCableKind, string> = {
  OUTER: 'Vnější / minikabely / závěsné',
  RISER: 'Riser (vnitřní)',
  BUNDLE: 'Svazky vláken',
};

export type FiberColorAssignment = {
  fiberNumber: number;
  bufferIndex: number;
  bufferColor: FiberBufferColorId;
  positionInBuffer: number;
  fiberColor: FiberColorId;
  stripe: boolean;
  profile: FiberColorProfileId;
  cableKind: FiberCableKind;
};

export const FIBER_COLOR_CODE_ERRORS = [
  'INVALID_FIBER_NUMBER',
  'INVALID_PROFILE',
  'INVALID_CABLE_KIND',
  'INVALID_BUFFER',
  'INVALID_FIBER_COLOR',
] as const;
export type FiberColorCodeError = (typeof FIBER_COLOR_CODE_ERRORS)[number];

export type FiberColorCodeResult =
  | { ok: true; value: FiberColorAssignment }
  | { ok: false; error: FiberColorCodeError };

export function isFiberColorProfileId(value: string | null | undefined): value is FiberColorProfileId {
  return (FIBER_COLOR_PROFILES as readonly string[]).includes((value ?? '').trim());
}

export function isFiberCableKind(value: string | null | undefined): value is FiberCableKind {
  return (FIBER_CABLE_KINDS as readonly string[]).includes((value ?? '').trim());
}

export function isFiberColorId(value: string | null | undefined): value is FiberColorId {
  return (FIBER_COLOR_IDS as readonly string[]).includes((value ?? '').trim());
}

/** Investor ze specifikace polygonu (`stavebnik`). Obsahuje „T-Mobile“ → T_MOBILE_CZ. */
export function fiberColorProfileForInvestor(investor: string | null | undefined): FiberColorProfileId {
  const raw = (investor ?? '').trim();
  if (!raw) return 'DEFAULT';
  return /t[\s-]*mobile/i.test(raw) ? 'T_MOBILE_CZ' : 'DEFAULT';
}

export function fiberColorOrderFor(
  profile: FiberColorProfileId,
  cableKind: FiberCableKind = 'OUTER',
): FiberColorOrder12 {
  return PROFILE_ORDERS[profile][cableKind];
}

function parseProfile(profile: string | null | undefined): FiberColorProfileId | null {
  const key = (profile ?? '').trim();
  return isFiberColorProfileId(key) ? key : null;
}

function parseCableKind(cableKind: string | null | undefined, fallback: FiberCableKind = 'OUTER'): FiberCableKind | null {
  if (cableKind == null || cableKind === '') return fallback;
  const key = cableKind.trim();
  return isFiberCableKind(key) ? key : null;
}

function bufferColorAt(index1: number): FiberBufferColorId | null {
  if (!Number.isInteger(index1) || index1 < 1 || index1 > FIBER_BUFFER_COUNT) return null;
  return FIBER_BUFFER_COLORS[index1 - 1];
}

function bufferIndexOfColor(color: string): number | null {
  const key = color.trim();
  const idx = (FIBER_BUFFER_COLORS as readonly string[]).indexOf(key);
  return idx >= 0 ? idx + 1 : null;
}

function assignment(
  n: number,
  profile: FiberColorProfileId,
  cableKind: FiberCableKind,
): FiberColorAssignment {
  const bufferIndex = Math.ceil(n / FIBERS_PER_BUFFER);
  const positionInBuffer = ((n - 1) % FIBERS_PER_BUFFER) + 1;
  const order = fiberColorOrderFor(profile, cableKind);
  const fiberColor = order[(n - 1) % FIBER_COLORS_PER_GROUP];
  return {
    fiberNumber: n,
    bufferIndex,
    bufferColor: FIBER_BUFFER_COLORS[bufferIndex - 1],
    positionInBuffer,
    fiberColor,
    stripe: positionInBuffer >= FIBER_STRIPE_FROM_POSITION,
    profile,
    cableKind,
  };
}

export function fiberNumberToColors(
  n: number,
  profile: FiberColorProfileId | string,
  cableKind: FiberCableKind | string = 'OUTER',
): FiberColorCodeResult {
  const parsedProfile = parseProfile(profile);
  if (!parsedProfile) return { ok: false, error: 'INVALID_PROFILE' };
  const parsedKind = parseCableKind(cableKind);
  if (!parsedKind) return { ok: false, error: 'INVALID_CABLE_KIND' };
  if (!Number.isInteger(n) || n < 1 || n > FIBER_NUMBER_MAX) {
    return { ok: false, error: 'INVALID_FIBER_NUMBER' };
  }
  return { ok: true, value: assignment(n, parsedProfile, parsedKind) };
}

export function colorsToFiberNumber(
  buffer: FiberColorId | number,
  color: FiberColorId | string,
  stripe: boolean,
  profile: FiberColorProfileId | string,
  cableKind: FiberCableKind | string = 'OUTER',
): FiberColorCodeResult {
  const parsedProfile = parseProfile(profile);
  if (!parsedProfile) return { ok: false, error: 'INVALID_PROFILE' };
  const parsedKind = parseCableKind(cableKind);
  if (!parsedKind) return { ok: false, error: 'INVALID_CABLE_KIND' };

  let bufferIndex: number | null = null;
  if (typeof buffer === 'number') {
    bufferIndex = Number.isInteger(buffer) ? buffer : null;
    if (bufferColorAt(bufferIndex ?? 0) == null) return { ok: false, error: 'INVALID_BUFFER' };
  } else if (typeof buffer === 'string') {
    bufferIndex = bufferIndexOfColor(buffer);
    if (bufferIndex == null) return { ok: false, error: 'INVALID_BUFFER' };
  } else {
    return { ok: false, error: 'INVALID_BUFFER' };
  }
  if (bufferIndex == null) return { ok: false, error: 'INVALID_BUFFER' };

  const fiberColor = (color ?? '').trim();
  if (!isFiberColorId(fiberColor)) return { ok: false, error: 'INVALID_FIBER_COLOR' };
  const order = fiberColorOrderFor(parsedProfile, parsedKind);
  const colorIndex = (order as readonly string[]).indexOf(fiberColor);
  if (colorIndex < 0) return { ok: false, error: 'INVALID_FIBER_COLOR' };

  const positionInBuffer = colorIndex + 1 + (stripe ? FIBER_COLORS_PER_GROUP : 0);
  const n = (bufferIndex - 1) * FIBERS_PER_BUFFER + positionInBuffer;
  return { ok: true, value: assignment(n, parsedProfile, parsedKind) };
}

export function fiberNumberToColorsOrNull(
  n: number,
  profile: FiberColorProfileId | string,
  cableKind: FiberCableKind | string = 'OUTER',
): FiberColorAssignment | null {
  const result = fiberNumberToColors(n, profile, cableKind);
  return result.ok ? result.value : null;
}

export function colorsToFiberNumberOrNull(
  buffer: FiberColorId | number,
  color: FiberColorId | string,
  stripe: boolean,
  profile: FiberColorProfileId | string,
  cableKind: FiberCableKind | string = 'OUTER',
): FiberColorAssignment | null {
  const result = colorsToFiberNumber(buffer, color, stripe, profile, cableKind);
  return result.ok ? result.value : null;
}
